import { useState } from 'react';
import { MessageSquare } from 'lucide-react';
import {
  Field,
  SectionCard,
  inputBase,
} from './FormPromptControls.tsx';

export const PERSONALITY_OPTIONS = [
  'Amigável e informal',
  'Profissional e objetivo',
  'Próximo e descontraído',
  'Personalizado',
];

type PersonalityFormFields = {
  nome_do_agente: string;
  personalidade_principal: string;
  uso_de_emojis: string;
  caracteristicas: string;
  principios: string;
  vicios_de_linguagem: string;
};

type PersonalityStepProps = {
  form: PersonalityFormFields;
  errors: Record<string, string>;
  personalityPreview: string;
  updateField: <K extends keyof PersonalityFormFields>(
    key: K,
    value: PersonalityFormFields[K],
  ) => void;
};

const PersonalityStep = ({
  form,
  errors,
  personalityPreview,
  updateField,
}: PersonalityStepProps) => {
  const [showVoiceAdvanced, setShowVoiceAdvanced] = useState(false);

  return (
    <SectionCard
      title="Como a atendente virtual deve conversar"
      icon={MessageSquare}
      hint="Personalize como a IA vai se comunicar: formal, informal, com mais ou menos emojis."
      description="Defina como a IA fala com o cliente."
    >
      <div className="grid gap-4 md:grid-cols-3">
        <Field
          label="Nome da atendente virtual"
          error={errors.nome_do_agente}
          errorKey="nome_do_agente"
          tooltip="Nome que a IA usa para se apresentar no início da conversa."
        >
          <input
            className={inputBase}
            value={form.nome_do_agente}
            onChange={(e) => updateField('nome_do_agente', e.target.value)}
            placeholder="Ex.: Ana"
          />
        </Field>
        <Field label="Tom de voz" error={errors.personalidade_principal} errorKey="personalidade_principal">
          <select
            className={inputBase}
            value={form.personalidade_principal}
            onChange={(e) => updateField('personalidade_principal', e.target.value)}
          >
            <option value="">Selecione</option>
            {form.personalidade_principal && !PERSONALITY_OPTIONS.includes(form.personalidade_principal) && (
              <option value={form.personalidade_principal}>{form.personalidade_principal}</option>
            )}
            {PERSONALITY_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </Field>
        <Field
          label="Uso de emojis"
          error={errors.uso_de_emojis}
          errorKey="uso_de_emojis"
          tooltip="Controla o estilo das mensagens. Se tiver dúvida, escolha 'Usar ocasionalmente'."
        >
          <select
            className={inputBase}
            value={form.uso_de_emojis}
            onChange={(e) => updateField('uso_de_emojis', e.target.value)}
          >
            <option value="">Selecione</option>
            <option value="Nunca usar">Não usar</option>
            <option value="Usar ocasionalmente">Usar ocasionalmente</option>
            <option value="Sempre usar">Usar com frequência</option>
          </select>
        </Field>
      </div>
      <button
        type="button"
        onClick={() => setShowVoiceAdvanced((prev) => !prev)}
        className="text-xs font-semibold text-teal-700 hover:text-teal-800"
      >
        {showVoiceAdvanced ? 'Ocultar ajustes avançados' : 'Mostrar ajustes avançados (opcional)'}
      </button>
      <div className="rounded-xl border border-emerald-100 bg-emerald-50/70 p-4" aria-live="polite">
        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Prévia da resposta</p>
        <p className="mt-2 text-sm text-slate-700">{personalityPreview}</p>
        {form.personalidade_principal === 'Personalizado' && (
          <p className="mt-2 text-xs text-slate-500">
            No tom personalizado, a IA segue as características e princípios definidos nos ajustes avançados abaixo.
          </p>
        )}
      </div>
      {showVoiceAdvanced && (
        <>
          <p className="text-xs text-slate-500">
            Se não preencher, a IA usa um tom neutro e humano.
          </p>
          <Field label="Características (opcional)" errorKey="caracteristicas">
            <input
              className={inputBase}
              value={form.caracteristicas}
              onChange={(e) => updateField('caracteristicas', e.target.value)}
              placeholder="Ex.: rápido, educado, objetivo"
            />
          </Field>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Princípios (opcional)" errorKey="principios">
              <input
                className={inputBase}
                value={form.principios}
                onChange={(e) => updateField('principios', e.target.value)}
                placeholder="Ex.: clareza, respeito, honestidade"
              />
            </Field>
            <Field label="Jeito de falar (opcional)" errorKey="vicios_de_linguagem">
              <input
                className={inputBase}
                value={form.vicios_de_linguagem}
                onChange={(e) => updateField('vicios_de_linguagem', e.target.value)}
                placeholder="Ex.: linguagem gaúcha leve, sem gírias pesadas"
              />
            </Field>
          </div>
        </>
      )}
    </SectionCard>
  );
};

export default PersonalityStep;
